//@ts-check

class PaymentApprovalService {
	constructor($http, Utility, $timeout, $rootScope, ContentService, LocalStorageService, InternalLinksService) {
		this.$http = $http;
		this.Utility = Utility;
		this.$timeout = $timeout;
		this.$rootScope = $rootScope;
		this.ContentService = ContentService;
		this.LocalStorageService = LocalStorageService;
		this.InternalLinksService = InternalLinksService;

		this.init();
	}

	init() {
		this.approvalsData = undefined;
		this.numberOfResults = 0;
		this.approvalsRequestSeq = 0;
		this.InternalLinksService.getLinks('/api/payments/links');
	}

	/**
	 * The property the user has selected now (the top bar's), upper case
	 * @return {string}
	 */
	_selectedSchemeRef() {
		let ref = this.LocalStorageService.retrieve('selectedBuildingRef');
		if (ref && typeof ref === 'object') {
			ref = ref.SchemeReference;
		}
		return (ref || '').toString().trim().toUpperCase();
	}

	/**
	 * Whether a reply for this property may replace what is shown. A reply that arrives after
	 * the user has moved to another property (a slow load, or the reload after an approval)
	 * used to replace that property's list with this one's, under the other's name.
	 * @param {string} schemeRef The property the request was for
	 * @return {boolean}
	 */
	_isForSelectedProperty(schemeRef) {
		const selected = this._selectedSchemeRef();
		return !selected || selected === (schemeRef || '').toString().trim().toUpperCase();
	}

	/**
	 * Method for fetching payments to be approved
	 * @param {string} schemeRef The building scheme reference
	 * @return {Promise<Object>} The payments promise
	 */
	async getPaymentApprovals(schemeRef) {
		let promise, promiseHandler;
		const seq = ++this.approvalsRequestSeq;
		const isCurrent = () => seq === this.approvalsRequestSeq && this._isForSelectedProperty(schemeRef);

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApi');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApi');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });

			if (!Array.isArray(results.data)) {
				throw new Error('The payments to approve could not be loaded.');
			}

			const currentCount = results.data.length;
			const approvalsData = this._mapApprovals(results.data);

			if (isCurrent()) {
				const oldApprovalsData = this.approvalsData;

				if (oldApprovalsData && oldApprovalsData.hasOwnProperty('approvals') && oldApprovalsData.approvals) {
					oldApprovalsData.approvals = approvalsData;
				} else {
					this.approvalsData = $.extend({ approvals: approvalsData }, oldApprovalsData);
				}

				this.approvalsData.schemeRef = schemeRef;
				this.approvalsData.approvalsFailed = false;

				this.$rootScope.$broadcast('approvals-count-changed', currentCount);
			}

			this.$timeout(() => promiseHandler.resolve(approvalsData));
		} catch (err) {
			if (isCurrent()) {
				const data = this.approvalsData;
				const keep = !!(data && data.approvals && data.schemeRef === schemeRef);
				this.approvalsData = $.extend({}, data, {
					approvals: keep ? data.approvals : [],
					schemeRef,
					approvalsFailed: true
				});
			}
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for fetching approved payments
	 * @param {string} schemeRef The building scheme reference
	 * @return {Promise<Object>} The payments promise
	 */
	async getApprovedPayments(schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiApproved');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiApproved');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();

			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.get(url, { headers });
			const approvedData = results.data ? results.data : results;
			const oldApprovedData = this.approvalsData;

			if (oldApprovedData && oldApprovedData.hasOwnProperty('approved') && oldApprovedData.approved)
				oldApprovedData.approved = approvedData;
			else {
				this.approvalsData = $.extend({ approved: approvedData }, oldApprovedData);
			}
			this.$timeout(() => promiseHandler.resolve(approvedData));
		} catch (err) {
			this.approvalsData = $.extend({}, this.approvalsData, { approved: [] });
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for approving a payment
	 * @param {string} paymentId The payment ID
	 * @param {string} schemeRef The building scheme reference
	 * @param {{ reload: boolean, timeout: number }} [options] reload: false answers as soon as the server has, without reloading the approvals list first (the caller reloads it); timeout: ms to wait for the server before giving up (the rejection's xhrStatus is then 'timeout')
	 * @return {Promise<Object>} The results promise
	 */
	async approvePayment(paymentId, schemeRef, options) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!paymentId) {
				throw new Error('No payment id provided...');
			}
			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiApprove');
			}

			url = url.replace('{paymentId}', paymentId);
			const results = await this.$http.post(url, undefined, this._requestConfig(options));
			const { data } = results;

			if (!options || options.reload !== false) {
				await this.getPaymentApprovals(schemeRef);
			}

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * The $http config for a request made with these options: only a timeout, if one is given
	 * @param {{ timeout: number }} [options] The options
	 * @return {Object|undefined} The config
	 */
	_requestConfig(options) {
		return options && options.timeout > 0 ? { timeout: options.timeout } : undefined;
	}

	/**
	 * Method for approving a bulk of payments
	 * @param {string} schemeRef The building scheme reference
	 * @param {Array<string>} IDs The selected payment IDs for approval
	 * @param {{ reload: boolean, timeout: number }} [options] As for approvePayment
	 * @return {Promise<Object>} The results promise
	 */
	async bulkApprovePayments(schemeRef, IDs, options) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			if (!IDs) {
				throw new Error('No selected Payment IDs for approval provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiBulkApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiBulkApprove');
			}

			const payload = {
				PaymentIds: IDs
			};

			const results = await this.$http.post(url, payload, this._requestConfig(options));
			const { data } = results;

			if (!options || options.reload !== false) {
				await this.getPaymentApprovals(schemeRef);
			}

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for querying a payment
	 * @param {string} paymentId The payment Id of the payment to be queried
	 * @param {string} message The query message
	 * @param {string} schemeRef The building scheme reference
	 * @param {{ reload: boolean }} [options] reload: false answers as soon as the query is
	 * sent, without reloading the approvals list first (the caller reloads it)
	 * @return {Promise<Object>} The results promise
	 */
	async queryPayment(paymentId, message, schemeRef, options) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!message) {
				throw new Error('No Query message provided...');
			}
			if (!paymentId) {
				throw new Error('No Payment ID provided...');
			}
			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentsApiQuery');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentsApiQuery');
			}

			const payload = {
				Message: message,
				PaymentId: paymentId
			};

			const results = await this.$http.post(url, payload);
			const { data } = results;

			if (!options || options.reload !== false) {
				await this.getPaymentApprovals(schemeRef);
			}

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Method for fetching the payments content
	 * @return {Promise<string>} The html promise
	 */
	async loadContent() {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			const content = await this.ContentService.load('PaymentsContent', '/api/payments/links');
			this.$timeout(() => promiseHandler.resolve(content));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Helper Method for mapping payment IDs 
	 * @param {string} schemeRef
	 * @return {Array<string>} The payment IDs
	 */
	_getPaymentIds(schemeRef) {
		let ids = [];
		ids = _.map(this.approvalsData.approvals, 'PaymentId');

		return ids;
	}

	/**
	 * Method for un-approving a specific payment
	 * @param {string} paymentId The payment ID
	 * @return {Promise<Object>} Asynchronous process
	 */
	async unApprovePayment(paymentId, schemeRef) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			if (!paymentId) {
				throw new Error('No payment id provided...');
			}
			if (!schemeRef) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentUnApprove');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentUnApprove');
			}

			url = url.replace('{paymentId}', paymentId);
			const results = await this.$http.post(url);
			const { data } = results;

			await this.getPaymentApprovals(schemeRef);

			this.$timeout(() => promiseHandler.resolve(data));
		} catch (err) {
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	async searchPayments(webPaymentSearch) {
		let promise, promiseHandler;

		try {
			promise = new Promise((resolve, reject) => {
				promiseHandler = {
					reject,
					resolve
				};
			});

			const schemeRef = webPaymentSearch.SchemeReference;

			if (!schemeRef || schemeRef['length'] < 3) {
				throw new Error('No Building reference provided...');
			}

			let url = await this.InternalLinksService.getLink('PaymentSearch');

			if (!url) {
				await this.InternalLinksService.getLinks('/api/payments/links');
				url = await this.InternalLinksService.getLink('PaymentSearch');
			}

			url = url.replace('{schemeRef}', schemeRef).trim();
			const headers = this.Utility.headersWithContentType;

			const results = await this.$http.post(url, webPaymentSearch, { headers });

			const approvedData = results.data.Data ? results.data.Data : results;
			(Array.isArray(approvedData) ? approvedData : []).forEach(payment => this._addAttachmentExtensions(payment));
			const oldApprovedData = this.approvalsData;

			if (this._isForSelectedProperty(schemeRef)) {
				this.numberOfResults = results.data.NumberOfResults;

				if (oldApprovedData && oldApprovedData.hasOwnProperty('approved') && oldApprovedData.approved)
					oldApprovedData.approved = approvedData;
				else {
					this.approvalsData = $.extend({ approved: approvedData }, oldApprovedData);
				}
			}
			this.$timeout(() => promiseHandler.resolve(approvedData));
		} catch (err) {
			this.approvalsData = $.extend({}, this.approvalsData, { approved: [] });
			this.$timeout(() => promiseHandler.reject(err));
		}

		return promise;
	}

	/**
	 * Helper Method for mapping payment IDs 
	 * @param {Array<Object>} approvals The payment approvals
	 * @return {Array<string>} The payment IDs
	 */
	_mapApprovals(approvals) {
		approvals.forEach((payment, index) => {
			payment.Checked = payment.hasOwnProperty('Checked') ? payment.Checked : false;

			payment.isQuerying = false;
			payment.isApproving = false;
			payment.isUnApproving = false;

			this._addAttachmentExtensions(payment);
		});

		return approvals;
	}

	/**
	 * The documents server holds each file as its id plus its extension; the API gives the
	 * two apart. The payment search's results went without it, so their documents (the
	 * History list's) did not open.
	 * @param {Object} payment A WebPaymentApproval
	 */
	_addAttachmentExtensions(payment) {
		(payment.Attachments || []).forEach((attachment) => {
			const extension = attachment.DocumentExtension || '';
			const path = attachment.Path || '';
			if (extension && path.toLowerCase().slice(-extension.length) !== extension.toLowerCase()) {
				attachment.Path = `${path}${extension}`;
			}
		});
	}

}

export default /* @ngInject */ (
	$http,
	Utility,
	$timeout,
	$rootScope,
	ContentService,
	LocalStorageService,
	InternalLinksService
) =>
	new PaymentApprovalService(
		$http,
		Utility,
		$timeout,
		$rootScope,
		ContentService,
		LocalStorageService,
		InternalLinksService
	);
